import { IBusinessAdmin } from "./businessAdmin.interface";
import { Language } from "./language.enum";
import {
  BusinessOnlinePricing,
  OnlineCardSplitTemplate,
  OnlinePricingStructure,
} from "./onlinePricing.interface";
import { PricingTemplate } from "./partner.interface";
import {
  OnlinePricingTemplate,
  PartnerBucketGroups,
} from "./partnerDeal.interface";
import { IPaxTerminal } from "./paxTerminal.interface";
import { IUser } from "./user.interface";

export type BusinessMerchantServiceChargeFee = {
  name: string;
  value: number;
  valueType: "Fixed" | "Percentage";
};

export interface IBusinessFull {
  _id: string;
  businessName: string;
  businessTradeName: string;
  websiteURL?: string;
  supportPhone?: string;
  supportEmail?: string;
  catalog: string;
  transactionTags: string;
  rootUser: IUser;
  industry: string;
  businessType: BusinessType;
  payoutOptions: {
    schedule: "daily" | "weekly-wed" | "weekly-thur" | "weekly-fri" | "monthly";
  };
  address: {
    street: string;
    houseNumber: string;
    houseNumberSuffix: string;
    postcode: string;
    city: string;
    country: string;
  };
  // GeoJSON, `[lng, lat]`. Absent until the geocoding job has run for this
  // business, so every read has to tolerate it being missing.
  location?: { type: "Point"; coordinates: [number, number] };
  merchantCategoryCode: number;
  transactionInformation: {
    averageMonthlyVolume: number;
    averageTimeBetweenPurchaseAndDeliveryInDays: number;
    expectedAverageMonthlyNumberOfTransactions: number;
    expectedAverageTransactionAmount: number;
    expectedMaximumTransactionAmount: number;
  };
  merchantServiceChargeFees: BusinessMerchantServiceChargeFee[];
  // One row per online bucket. For a 16-bucket merchant `name` is a bucket
  // field identifier (OnlineBucketField); four-bucket merchants keep their
  // legacy display names.
  onlineMerchantServiceChargeFees?: BusinessMerchantServiceChargeFee[];
  onlinePricing?: BusinessOnlinePricing;
  // Which of the two online pricing shapes this merchant is priced on. Absent
  // on merchants predating the 16-bucket structure, which are four-bucket.
  onlinePricingStructure?: OnlinePricingStructure;
  // Set when an in-person-only pricing save remapped the merchant: its online
  // pricing has not been saved against the mapped online range yet, and the
  // next online save is banded against that range in full.
  onlinePricingRangePending?: boolean;
  // The two template axes the 16-bucket group set is the product of: which
  // card bucket is split out, and which local payment method is priced.
  onlineCardSplitTemplate?: OnlineCardSplitTemplate;
  onlinePricingTemplate?: OnlinePricingTemplate;
  softwareVendorOnlinePricing?: BusinessOnlinePricing;
  softwareVendorOnlinePricingTemplate?: OnlinePricingTemplate;
  noEdit?: string[];
  // The pricing save's own rule, from persisted Adyen state; see isNoEdit.ts.
  isSubmittedToAdyen?: boolean;
  // bankAccountHolderName: string;
  bankAccountIban?: string;
  bankAccountNumber?: string;
  bankName?: string;
  bankBranchNumber?: string;
  bic: string;
  bankAddress: {
    street: string;
    houseNumber: string;
    postcode: string;
    city: string;
    country: string;
  };
  referralCode: string;
  referrerCode: string;
  status: string;
  rapydOnboardingStatus?: string;
  onlinePaymentStatus?: OnlinePaymentStatus | string;
  phosTerminalToken: string;
  phosMerchantToken: string;
  phosUserToken: string;
  merchandId: string;
  mid: string;
  agreementId: string;
  terminalId: string;
  vat: number;
  createdAt: string;
  updatedAt: string;
  businessRegistrationNumber: string;
  vatNumber?: string;
  tinNumber?: string;
  persons: BusinessPerson[];
  onboardingError: {
    type: string;
    message: string;
  };
  mccComments?: string;
  generalComments?: string;
  incorporationDate?: string;
  complianceContact: ComplianceContact;
  salesRepresentative: string;
  salesRepresentativeExpDate: string;
  salesRepresentative2: string;
  salesRepresentative2ExpDate: string;
  salesBusinessDeveloper: string;
  salesBusinessDeveloperExpDate: string;
  directSalesLead: string;
  directSalesLeadExpDate: string;
  teamManager: string;
  teamManagerExpDate: string;
  customerSuccessManager: string;
  partnerManager: string;
  partnerManagerExpDate: string;
  onboardingFlags?: IOnboardingFlag[];
  acquirerOnboardingFlags?: IAcquirerOnboardingFlags;
  yazaraMerchantId: string;
  yazaraTerminals: IYazaraTerminal[];
  description?: string;
  settings: IBusinessSettings;
  partnerId?: string;
  partnership?: string;
  partnerPurchPriceProfileId: string;
  partnerOnlinePurchPriceProfileId?: string | null;
  softwareVendorPricing?: PartnerBucketGroups;
  softwareVendorPricingTemplate?: PricingTemplate;
  devices: IDevice[];
  verificationDate: string;
  firstTsxDate?: Date;
  externalOnboardingId?: string;
  adyen?: {
    accountHolderId?: string;
    balanceAccountId?: string;
    transferInstrumentId?: string;
    onboardingLink?: string;
    onboardingStatus?: OnlinePaymentStatus;
    // Present once live on Adyen; editing online pricing then triggers a resync.
    splitConfigurationId?: string;
  };
  rapydNew?: {
    merchantEwalletId?: string;
    settlementEwalletId?: string;
    pairings?: IRapydTerminalPairing[];
  };
  shift4?: {
    merchantId?: string;
    onboardingStatus?: Shift4OnboardingStatus;
    onboardingStatusUpdatedAt?: string;
    submittedAt?: string;
    lastFailureReason?: string;
  };
  files?: IBusinessFile[];
  mcc?: string;
  website?: string;
  phone?: string;
  features?: BusinessFeatureFilter[];
}

export enum BusinessSettingsEndOfShiftCalculationType {
  PerBusiness = "perBusiness",
  PerDevice = "perDevice",
  PerUser = "perUser",
}

export interface IBusinessSettings {
  locale?: Language;
  payments: {
    isOnlinePaymentsFeatureEnabled: boolean;
    isPayByQRFeatureEnabled: boolean;
    isPayAtTheTableFeatureEnabled: boolean;
    enableCashPayments: boolean;
    enableDiscounts: boolean;
    enableTips: boolean;
    optionalTipping: boolean; //default true
    customTipButtonDisabled: boolean;
    customTipButtonType: string;
    tipPercentagePresets: {
      percentage1: number;
      percentage2: number;
      percentage3: number;
      hidePercentage3: boolean;
      showCalculatedTip: boolean;
    };
    enableSkipTags: boolean;
    decimalFirstAmountEntry: boolean;
    refundCode: string;
    adminCode: string;
    limitEmployeeTransactionView: boolean;
    isDepositFeatureEnabled: boolean;
    isDepositEnabled: boolean;
    maxDepositDailySum: number;
    maxDepositAmount: number;
    blockDirectPayments: boolean;
    blockEmployeeRefunds: boolean;
    ataRawTransactionId?: boolean;
  };
  branding?: {
    businessLogoUrl: string;
  };
  idleScreen: {
    logoUrl: string;
    textColor: string;
    showLogoutButton: boolean;
    showTransactionListButton: boolean;
    logoutButtonText: {
      en: string;
      nl: string;
      fr: string;
      de: string;
      it: string;
    };
    logoutPopconfirmTitle: {
      en: string;
      nl: string;
      fr: string;
      de: string;
      it: string;
    };
    logoutPopconfirmSubtitle: {
      en: string;
      nl: string;
      fr: string;
      de: string;
      it: string;
    };
  };

  endOfShift: {
    enableEndOfShift: boolean; // if this is disabled, we don't show this feature to the merchant
    calculationType: BusinessSettingsEndOfShiftCalculationType; //calculation type
    calculateVolumeFromLastShift: boolean; // if this is true, the shift will always calculate the volume from the start of the day, if it is false it will calculate the volume from the last shift or if not shift exists than from the start of that day.
    requireEndOfShiftCode: boolean;
    endOfShiftCode: string; // 4-digit code for ending the shift
    shiftReportDefaultEmail: string;
    showTip: boolean;
  };
  endOfDay: {
    hour: number;
    minute: number;
  };
  receipt: {
    logoUrl: string;
    showAddress: boolean;
    extraText: string;
    qrUrl: string;
    showTip: boolean;
    hideVat: boolean;
  };

  insights?: {
    isInsightsFeatureEnabled: boolean;
  };

  reviews: {
    isReviewsFeatureEnabled: boolean;
    enableReviews: boolean;
    googlePlaceId: string;
    googleAccountId?: string;
    googleLocationId?: string;
    googleOAuthTokenExpirationDate?: Date;
    notificationEmails?: string[];
  };

  reports: {
    showTips: boolean;
    sendList: string[];
    isTagAnalyticsReportsEnabled?: boolean;
    types: {
      endOfDaySalesOverviewReport: IReportSettings;
      dailyPayoutReport: IReportSettings;
      monthlyAccountingReport: IReportSettings;
    };
  };
  invoices: {
    invoiceEmail: string;
  };
  posIntegration?: {
    allowPartnerDashboardAccess: boolean;
    isPosEmulatorEnabled?: boolean;
  };
  fiscalization?: {
    posRTConfig?: string | null;
  };
}

export interface IPosRTConfig {
  _id: string;
  country: string;
  portalUrl: string;
  acquirerCountry: string;
  acquirerFiscalCode: string;
  acquirerLegalName: string;
  fields: string[];
  createdAt: string;
  updatedAt: string;
}

export type IReportSettings = {
  isEnabled: boolean;
  scheduleType: ScheduleType;
};

export type ScheduleType =
  | "daily-end-of-day"
  | "daily-day-after"
  | "monthly-start-of-next-month"
  | "monthly-start-of-month";

// Mirrors backend IFormattedRapydNew pairings — `pairedBy` is stripped server-side
// before rapydNew is returned on business GETs.
export interface IRapydTerminalPairing {
  terminalReference: string;
  deviceId: string;
  merchantId: string;
  operationId?: string;
  currency: string;
  pairedAt: string;
}

export interface IYazaraTerminal {
  DID: string;
  TID: string;
  MID: string;
  createdAt: string;
  activationCode: string;
  isRandomDeviceId?: boolean;
}

export interface IOnboardingFlag {
  type:
    | "BUSINESS_TYPE_OTHER"
    | "PERSON_MISSING_ROLE"
    | "MISSING_PERSON"
    | "CUSTOM_PERSON"
    | "BUSINESS_ADDRESS_EDIT"
    | "PAYOUT_SCHEDULE_CHANGE"
    | "IDENTIFICATION_CHECK_NAME_MISMATCH"
    | "IBAN_CHECK_MISMATCH"
    | "IBAN_CHECK_SERVER_DOWN"
    | "PERSON_ADDRESS_DATA_MISSING";
  message: string;
  isResolved: boolean;
  personId?: string;
  adyenLegalEntityId?: string;
}

export interface IAcquirerOnboardingFlag {
  type: string;
  message: string;
  personId?: string;
  adyenLegalEntityId?: string;
}

export interface IAcquirerOnboardingFlags {
  adyen?: IAcquirerOnboardingFlag[];
  rapyd?: IAcquirerOnboardingFlag[];
  shift4?: IAcquirerOnboardingFlag[];
}
export enum ComplianceStatus {
  NotContacted = "Not Contacted",
  ReachedOut = "Reached Out",
  InProgress = "In Progress",
  Completed = "Completed",
}
export interface ComplianceContact {
  status: ComplianceStatus;
  dateOfContact: Date;
  teamMember: string;
  notes: string;
}

export type IBusinessFullQuery = IBusinessFull & {
  // rootUserEmail: string;
  // rootUserName: string;
  complianceStatus: ComplianceStatus;
  name: string;
  country: string;
  features: BusinessFeatureFilter[];
  onlinePaymentStatus: OnlinePaymentStatus;
  // Comma-joined, not arrays: API Gateway hands the lambda only the last
  // repetition of a query key, so a repeated param would lose every value but one.
  acquirers: string;
  acquirerStatuses: string;
};

/** Mirror of backend `AcquirerName` (shared/types/acquirer.interface.ts). */
export enum FilterableAcquirer {
  Rapyd = "rapyd",
  Adyen = "adyen",
  Shift4 = "shift4",
}

/** Mirror of backend `AcquirerStatusBucket` — one vocabulary for all acquirers. */
export enum AcquirerStatusBucket {
  PendingSubmission = "pendingSubmission",
  Submitted = "submitted",
  AcquirerReview = "acquirerReview",
  AwaitingMerchantAction = "awaitingMerchantAction",
  Approved = "approved",
  Rejected = "rejected",
}

export enum BusinessFeatureFilter {
  Online = "online",
  PayByQR = "payByQr",
  Tips = "tips",
  Deposits = "deposits",
  Reviews = "reviews",
  Cash = "cash",
  Insights = "insights",
}

/** Mirror of backend `AdyenOnboardingStatus` (shared/types/business.interface.ts).
 *  Drives the Online status badge in the KBM businesses table. */
export type OnlinePaymentStatus =
  | "onboardingAllowed"
  | "onboardingDisallowed"
  | "pendingToS"
  | "pending"
  | "awaitingData"
  | "verified"
  | "rejected"
  | "initiated"
  | "awaitingMerchantAction"
  | "verificationPending";

export type BusinessStatus =
  | "pendingSubmission"
  | "pendingOutreach"
  | "pendingVerification"
  | "verified"
  | "unverified"
  | "inactivated";

/** Mirror of backend `Shift4OnboardingStatus` (shared/types/shift4Onboarding.ts).
 *  `approved` is terminal; `failed` is the only state a resubmit may claim. */
export type Shift4OnboardingStatus =
  "submitted" | "created" | "underwriting" | "approved" | "failed" | "rejected";

export type IdentificationAttachment = {
  fileName: string;
  bucketName: string; //url
};

// Wire values match Adyen / backend `PersonIdentityDocumentType`. Optional so
// older rows and an older BE (field unknown / stripped) keep working.
export type PersonIdentityDocumentType =
  "passport" | "identityCard" | "driversLicense";

export type BusinessPerson = {
  // Mongo subdocument id. The handle Adyen document rows are attributed by.
  _id?: string;
  adyenLegalEntityId?: string;
  address: IAddress;
  dateOfBirth: string;
  email: string;
  firstName: string;
  lastName: string;
  // Front (upper) side of the identity document.
  identificationAttachment: IdentificationAttachment;
  // Back (lower) side of the identity document. Optional for backward
  // compatibility with persons uploaded before two-sided support existed.
  identificationAttachmentBack?: IdentificationAttachment;
  identificationDocumentType?: PersonIdentityDocumentType;
  ssn: string;
  phone: { number: string; countryCode: string };
  roles: BusinessRole[];
  isCustom: boolean;
};

export interface IAddress {
  street: string;
  houseNumber: string;
  houseNumberSuffix?: string;
  postcode: string;
  city: string;
  country: string;
}

export type BusinessRole =
  | "PrimaryContact"
  | "Director"
  | "AuthorizedSignatory"
  | "BoardOfDirectors"
  | "BeneficialOwner"
  | "VotingControl"
  | "PersonShareOwner";

export type BusinessType =
  | "soleTrader"
  | "publicLimitedCompany"
  | "privateLimitedCompany"
  | "partnership";

export type DeviceType = "Android" | "IOS" | "Sunmi" | "Pax";

export interface IDevice {
  type: DeviceType;
  deviceId: string;
  deviceName?: string;
  model: string;
  shortId: string;
  manufacturer: string;
  lastOnline: string;
  _id: string;
  createdAt: string;
  updatedAt: string;
  buildName: string;
  buildNumber: string;
  paxTerminalInfo?: IPaxTerminal;
}

export enum BucketName {
  DomesticDebit = "Domestic Debit",
  DomesticCredit = "Domestic Credit",
  EuropeanDebit = "European Debit",
  EuropeanCredit = "European Credit",
  DomesticCorporateDebit = "Domestic Corporate Debit",
  DomesticCorporateCredit = "Domestic Corporate Credit",
  EuropeanCorporateCredit = "European Corporate Credit",
  EuropeanCorporateDebit = "European Corporate Debit",
  InternationalDebit = "International Debit",
  InternationalCredit = "International Credit",
  InternationalCorporateDebit = "International Corporate Debit",
  InternationalCorporateCredit = "International Corporate Credit",
  ForeignCreditCards = "Foreign credit cards",
  ForeignDebitCards = "Foreign debit cards",
  InPersonAmex = "Amex",
}

export enum BucketNameField {
  DomesticDebit = "domesticDebit",
  DomesticCredit = "domesticCredit",
  EuropeanDebit = "europeanDebit",
  EuropeanCredit = "europeanCredit",
  DomesticCorporateDebit = "domesticCorporateDebit",
  DomesticCorporateCredit = "domesticCorporateCredit",
  EuropeanCorporateCredit = "europeanCorporateCredit",
  EuropeanCorporateDebit = "europeanCorporateDebit",
  InternationalDebit = "internationalDebit",
  InternationalCredit = "internationalCredit",
  InternationalCorporateDebit = "internationalCorporateDebit",
  InternationalCorporateCredit = "internationalCorporateCredit",
  ForeignCreditCards = "foreignCreditCards",
  ForeignDebitCards = "foreignDebitCards",
  InPersonAmex = "inPersonAmex",
}

export interface IBusinessFile {
  _id: string;
  name: string;
  type: string;
  url: string;
  uploadedAt: string;
}
